import { Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import Image from 'img/aside-promo-modal/referral/referral-program-block-promo.png';
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { IS_PAID_USER } from 'reactApp/appHelpers/configHelpers';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { DEMO_PROMO_VARIANT } from 'reactApp/appHelpers/featuresHelpers/features/demoPromo';
import {
    goToReferralSubscriptions,
    REFERRAL_ANALYTIC_NAME,
    REFERRAL_PROMO_BLOCK_HITS,
    REFERRAL_PROMO_BLOCK_PROMO_ID,
    referralProgramBonusQuota,
} from 'reactApp/appHelpers/featuresHelpers/features/referralProgram';
import { selectAllBusinessTemplates } from 'reactApp/modules/businessTemplates/businessTemplates.selectors';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { type DemoPromoTooltipData, EPromoType } from 'reactApp/modules/promo/promo.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './DemoPromo.css';
import { DemoPromoTooltip } from './DemoPromoTooltip/DemoPromoTooltip';

type DemoPromoCfg = {
    className?: string;
    onClick: () => void;
    onShow?(): void;
    text: string;
    icon: React.ReactNode;
    tooltipData: DemoPromoTooltipData | null;
};

const useDemoPromoCfg = () => {
    const tooltipData = useSelector(PromoSelectors.pullPromo(EPromoType.demoPromoTooltip)) || null;
    const navigate = useNavigate();
    const businessTemplatesCategories = useSelector(selectAllBusinessTemplates);

    const storage = useSelector(getCurrentStorage);
    const { isHome } = getStorage(storage);

    return useMemo<DemoPromoCfg | null>(() => {
        // eslint-disable-next-line sonarjs/no-small-switch
        switch (DEMO_PROMO_VARIANT) {
            case 'business-templates':
                // Не показыаем промо если нет шаблонов, которые промоутим
                if (!businessTemplatesCategories.length) {
                    return null;
                }

                return {
                    text: 'Шаблоны для бизнеса',
                    onClick: () => {
                        navigate('/templates');
                        sendPaymentGa({
                            eventCategory: 'click_go_to_biz_template',
                            is_mailspace: IS_PAID_USER,
                        });
                        tooltipData?.onClose();
                    },
                    icon: (
                        <div className={styles.businessTemplatesIcon}>
                            <div className={styles.businessTemplatesIconBackdrop}>
                                <img src="https://imgs2.imgsmail.ru/static/cloud/business-templates/emoji_doc.png" />
                            </div>
                        </div>
                    ),
                    tooltipData,
                };
            // tempexp_18086-start
            case 'referal-program':
                // Показываем только на хомяке
                if (!isHome) {
                    return null;
                }
                return {
                    className: styles.referralProgram,
                    text: `+${referralProgramBonusQuota.value} бонусов за друга`,
                    onClick: () => {
                        storeHelper.setValue(REFERRAL_PROMO_BLOCK_HITS, 1);
                        emitAnalyticEvent(AnalyticEventNames.REFERRAL_BANNER_CLICK);
                        sendPaymentGa({
                            action: 'click',
                            eventCategory: ECategoryGa.banner,
                            name: REFERRAL_ANALYTIC_NAME,
                        });
                        goToReferralSubscriptions();
                    },
                    onShow: () => {
                        storeHelper.clearStore(REFERRAL_PROMO_BLOCK_HITS);
                        storeHelper.markAsShown(REFERRAL_PROMO_BLOCK_PROMO_ID);
                        emitAnalyticEvent(AnalyticEventNames.REFERRAL_BANNER_SHOW);
                        sendPaymentGa({
                            action: 'view',
                            eventCategory: ECategoryGa.banner,
                            name: REFERRAL_ANALYTIC_NAME,
                        });
                    },
                    icon: (
                        <div className={styles.referralProgramIcon}>
                            <img src={Image} />
                        </div>
                    ),
                    tooltipData: null,
                };
            // tempexp_18086-end
            default:
                return null;
        }
    }, [businessTemplatesCategories.length, navigate, tooltipData, isHome]);
};

const DemoPromoInner = ({ setVisible }: { setVisible: (visibility: boolean) => void }) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const cfg = useDemoPromoCfg();

    useEffect(() => {
        if (!cfg) {
            setVisible(false);
        }
    }, [cfg]);

    useEffect(() => {
        cfg?.onShow?.();
    }, []);

    if (!cfg) {
        return null;
    }

    const { onClick, text, icon, tooltipData, className } = cfg;

    return (
        <div className={classNames(styles.root, className)} ref={rootRef}>
            <div className={styles.wrapper} onClick={onClick}>
                <Text className={styles.text}>{text}</Text>
                <div className={classNames(styles.iconWrapper)}>{icon}</div>
            </div>
            {tooltipData && <DemoPromoTooltip {...tooltipData} target={rootRef} />}
        </div>
    );
};

export const DemoPromo = React.memo(React.forwardRef(DemoPromoInner));
